import { Fragment, useCallback, useMemo } from "react"
import {
  Box,
  Button,
  useTheme,
  Typography,
  CardContent,
  Divider,
  CardMedia,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  StepConnector,
  Stack,
} from "@mui/material"
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded"
import { deepmerge } from "@mui/utils"
import { Link } from "react-router-dom"
import { Trans } from "react-i18next"
import { getIcon } from "utils/icon"
import { useTranslation } from "providers/i18n"
import { useVenue } from "providers/venue"
import { useDirectionOverview } from "hooks/direction/useDirectionOverview"
import { useDirectionOverviewMap } from "hooks/direction/useDirectionOverviewMap"
import { useMobileUrl } from "hooks/useMobileUrl"
import {
  CloseIcon,
  NearMeIcon,
  OriginMarkerIcon,
  DestinationMarkerIcon,
  WheelchairIcon,
} from "components/icons"
import { PageCard } from "components/card"
import { QRCode } from "components/QRCode"
import { MotionDiv } from "components/motions"
import { MyLocationButton } from "components/Button/MyLocationButton"
import { OrdinalSelector } from "components/OrdinalSelector"
import { CloseIconButton } from "components/Button"
import { StepByStepIcon } from "components/Progress/StepIcon"
import { useAppUI } from "providers/ui"
import { SkeletonDirectionOverviewSidebar } from "components/domains/direction/kiosk-h"
import { usePageSideBarLayout } from "providers/ui/hooks"
import { IContentWithAsideProps } from "layouts/kiosk-h/ContentWithAside"
import { BottomSidebarAction } from "components/BottomSidebarAction/BottomSidebarAction"

interface IDirectionOverviewKioskHPageProps {
  Layout?: React.FC<IContentWithAsideProps>
}

export const DirectionOverviewKioskHPage = ({
  Layout = Box,
}: IDirectionOverviewKioskHPageProps) => {
  const { isMultiOrdinalVenue } = useVenue()
  const {
    currentOrdinal,
    route,
    originData,
    elevatorPreferredMode,
    relatedLevelWithSteps,
    changeOrdinal,
    startNavigate,
    toggleElevatorPreference,
    originName,
    destinationData,
    destinationName,
    distance,
    duration,
    overviewSteps,
  } = useDirectionOverview()
  const lastStep = overviewSteps.length - 1
  const stepsAvailable = overviewSteps && overviewSteps.length > 0

  useDirectionOverviewMap(route, currentOrdinal)

  const { goBack } = useAppUI()
  const { mobileUrl } = useMobileUrl()
  const { t } = useTranslation()

  usePageSideBarLayout({
    sidebar: {
      open: true,
    },
  })

  const theme = useTheme()

  const {
    root,
    contentContainer,
    closeButton,
    titleText,
    stepContainer,
    stepListAvatarContainer,
    stepListContainer,
    stepListItem,
    stepListItemText,
    connector,
    logoImage,
    listItemIcon,
    actionContainer,
    avoidStepButton,
    travelInfoContainer,
    routeDescription,
    highlightText,
    qrCodeContainer,
    qrCodeCaptionContainer,
    qrcodePrimaryText,
    qrcodeSecondaryText,
    qrCode,
    startButton,
    pageCardSideBar,
    sidebarContent,
    primaryText,
    secondaryText,
    divider,
    separator,
    locationButtonContainer,
    ordinalSelectMenu,
    routeDetailContainer,
    durationContainer,
    stepsTitleText,
    routeDescriptionStack,
    estimateTimeText,
    bottomSidebarAction,
  } = theme.components[`AppDirectionOverview`]?.styleOverrides || {}

  const { destinationIcon, originIcon } =
    theme.components[`AppNavigationStep`]?.styleOverrides || {}

  const sideBarStyleOverrides =
    theme.components[`AppSidebar`]?.styleOverrides || {}
  const { wrapper, body } = sideBarStyleOverrides

  const startIcon = useMemo(
    () => getIcon(theme?.assets?.navigation?.startIcon) || <NearMeIcon />,
    [theme]
  )

  const closeIcon = useMemo(
    () => getIcon(theme?.assets?.closeIcon) || <CloseIcon />,
    [theme]
  )

  const separatorIcon = useMemo(
    () =>
      getIcon(theme?.assets?.navigation?.separator, separator) || (
        <KeyboardArrowRightRoundedIcon sx={separator} />
      ),
    [theme, separator]
  )

  const originMarkerIcon = useMemo(
    () =>
      getIcon(theme?.assets?.navigation?.origin, originIcon) || (
        <OriginMarkerIcon sx={originIcon} />
      ),
    [theme, originIcon]
  )

  const { highlightedText } =
    theme.components[`DirectionStep`]?.styleOverrides || {}

  const highlightedStepText = (
    <Typography variant="subTitle" component="span" sx={highlightedText} />
  )

  const destinationStepIcon = useMemo(
    () =>
      getIcon(theme?.assets?.directionStep?.destination, destinationIcon) || (
        <DestinationMarkerIcon
          color="error"
          fontSize="small"
          sx={destinationIcon}
        />
      ),
    [theme, destinationIcon]
  )

  const handleClose = useCallback(() => {
    goBack()
  }, [goBack])

  return (
    <Layout
      aside={
        <>
          <PageCard sx={pageCardSideBar}>
            <MotionDiv
              sx={deepmerge(wrapper, sidebarContent)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.25 }}
            >
              <Box sx={body}>
                {!stepsAvailable ? (
                  <SkeletonDirectionOverviewSidebar />
                ) : (
                  <Box sx={root}>
                    <CardContent sx={contentContainer}>
                      <CloseIconButton
                        sx={closeButton}
                        icon={closeIcon}
                        onClick={handleClose}
                        size="large"
                      />
                      <Typography sx={titleText}>
                        {t("Direction Summary Header")}
                      </Typography>
                      <Box sx={travelInfoContainer}>
                        <Box sx={routeDescription}>
                          <Stack sx={routeDescriptionStack}>
                            <Typography variant="h6" component="span">
                              {t("from")}
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={highlightText}
                              component="span"
                            >
                              {t(originName)}
                            </Typography>
                          </Stack>
                          {separatorIcon}
                          <Stack sx={routeDescriptionStack}>
                            <Typography variant="h6" component="span">
                              {t("to")}
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={highlightText}
                              component="span"
                            >
                              {t(destinationName)}
                            </Typography>
                          </Stack>
                        </Box>
                        <Divider sx={divider} />
                        <FormControlLabel
                          sx={avoidStepButton}
                          control={
                            <Switch
                              checked={elevatorPreferredMode}
                              onChange={toggleElevatorPreference}
                            />
                          }
                          label={
                            <>
                              <WheelchairIcon />
                              {t("Avoid Steps")}
                            </>
                          }
                        />
                        <Box sx={routeDetailContainer}>
                          <Typography sx={estimateTimeText}>
                            {t("Estimated travel time")}
                          </Typography>
                          <Box sx={durationContainer}>
                            <Typography
                              variant="h2"
                              component="span"
                              sx={primaryText}
                            >
                              {t("Travel Duration", { duration })}
                            </Typography>
                            <Typography
                              variant="h4"
                              component="span"
                              sx={secondaryText}
                            >
                              {"— "}
                              {t("Travel Distance", { distance })}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      <Box sx={stepContainer}>
                        <Typography sx={stepsTitleText}>
                          {t("Steps")}
                        </Typography>
                        <List sx={stepListContainer}>
                          {originData && (
                            <ListItem sx={stepListItem}>
                              <ListItemAvatar sx={stepListAvatarContainer}>
                                {originMarkerIcon}
                              </ListItemAvatar>
                              <ListItemIcon sx={listItemIcon}>
                                <CardMedia
                                  component="img"
                                  image={originData.venueLogo}
                                  sx={logoImage}
                                />
                              </ListItemIcon>
                              <ListItemText
                                sx={stepListItemText}
                                primary={t(originName)}
                              />
                            </ListItem>
                          )}
                          <StepConnector sx={connector} />
                          {overviewSteps.map((step, index) => {
                            const { description } = step
                            return (
                              <Fragment key={`step-${index}`}>
                                <ListItem
                                  sx={stepListItem}
                                  component={Link}
                                  to={`navigate/steps/${
                                    index + 1
                                  }?avoidSteps=${elevatorPreferredMode}`}
                                >
                                  <ListItemAvatar sx={stepListAvatarContainer}>
                                    <StepByStepIcon
                                      index={index}
                                      step={step}
                                      lastStep={lastStep}
                                    />
                                  </ListItemAvatar>
                                  <ListItemText
                                    sx={stepListItemText}
                                    primary={
                                      <Trans
                                        components={{
                                          category: highlightedStepText,
                                          destination: highlightedStepText,
                                          landmark: highlightedStepText,
                                          turn: highlightedStepText,
                                        }}
                                      >
                                        {t(description.text, {
                                          category: t(
                                            description.options?.category
                                          ),
                                          destination: t(
                                            description.options?.destination
                                          ),
                                          landmark: t(
                                            description.options?.landmark
                                          ),
                                          turn: t(description.options?.turn),
                                        })}
                                      </Trans>
                                    }
                                  />
                                </ListItem>
                                <StepConnector sx={connector} />
                              </Fragment>
                            )
                          })}
                          {destinationData && (
                            <ListItem sx={stepListItem}>
                              <ListItemAvatar sx={stepListAvatarContainer}>
                                {destinationStepIcon}
                              </ListItemAvatar>
                              {destinationData.venueLogo && (
                                <ListItemIcon sx={listItemIcon}>
                                  <CardMedia
                                    component="img"
                                    image={destinationData.venueLogo}
                                    sx={logoImage}
                                  />
                                </ListItemIcon>
                              )}
                              <ListItemText
                                sx={stepListItemText}
                                primary={t("Arrived at destination")}
                              />
                            </ListItem>
                          )}
                        </List>
                      </Box>
                      <Box sx={qrCodeContainer}>
                        <QRCode url={mobileUrl} sx={qrCode} />
                        <Box sx={qrCodeCaptionContainer}>
                          <Typography
                            variant="subtitle2"
                            component="p"
                            sx={qrcodePrimaryText}
                          >
                            {t("Scan Here")}
                          </Typography>
                          <Typography
                            variant="subtitle2"
                            component="p"
                            sx={qrcodeSecondaryText}
                          >
                            {t("For Mobile")}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={actionContainer}>
                        <Button
                          variant="contained"
                          sx={startButton}
                          onClick={startNavigate}
                          startIcon={startIcon}
                          className="highlight"
                        >
                          {t("Start")}
                        </Button>
                      </Box>
                    </CardContent>
                  </Box>
                )}
              </Box>
            </MotionDiv>
          </PageCard>
          <BottomSidebarAction sx={bottomSidebarAction} />
        </>
      }
    >
      {isMultiOrdinalVenue && (
        <Box sx={ordinalSelectMenu}>
          <OrdinalSelector
            levels={relatedLevelWithSteps}
            onClickOrdinal={changeOrdinal}
            selectedOrdinal={currentOrdinal}
          />
        </Box>
      )}
      <Box sx={locationButtonContainer}>
        <MyLocationButton
          component={Link}
          to={`/maps/dir/unknown/${destinationData?.id}`}
        />
      </Box>
    </Layout>
  )
}
